import './util/yupLocale';
import { ApolloClient, ApolloProvider, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { ApolloProvider as ApolloHooksProvider } from '@apollo/react-hooks';
import { BrowserRouter, Redirect } from 'react-router-dom';
import { GameContextProvider } from './contexts/GameContext';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ProfileContext, ProfileContextProvider } from './contexts/ProfileContext';
import { Route, RouteProps, Switch } from 'react-router';
import { StopWatchContextProvider } from './contexts/StopWatchContext';
import { gamesConfiguration } from './configuration/gamesConfiguration';
import { removeTypenameFromMutationLink } from 'apollo-remove-typename-mutation-link';
import Admin from './pages/common/admin/Admin';
import ErrorBoundary from './components/common/wrappers/ErrorBoundary';
import ErrorPage from './pages/common/ErrorPage';
import Game from './pages/common/Game';
import GameCodeExpired from './pages/common/GameCodeExpired';
import GameCodeInvalid from './pages/common/GameCodeInvalid';
import GameCodeNotValidYet from './pages/common/GameCodeNotValidYet';
import GameCodeUsed from './pages/common/GameCodeUsed';
import GameInProgress from './pages/common/GameInProgress';
import GameSelect from './pages/common/GameSelect';
import Home from './pages/common/Home';
import Leaderboard from './pages/common/admin/Leaderboard';
import Login from './pages/common/admin/Login';
import React, { useContext, useEffect } from 'react';
import RedirectToHome from './components/common/RedirectToHome';
import ScrollTop from './components/common/wrappers/ScrollTop';
import ZdarKidsWorkbookSolution from './components/games/zdar-kids-game/ZdarKidsWorkbookSolution';

export const serverUri = process.env.NODE_ENV === 'production' ? '' : process.env.REACT_APP_BE_URI;
const httpLink = createHttpLink({ uri: serverUri + '/graphql' });
const link = from([removeTypenameFromMutationLink, httpLink]);

const apolloClient = new ApolloClient({
    link,
    cache: new InMemoryCache(),
});

export default function App() {
    useEffect(() => {
        window.onpageshow = function (event: PageTransitionEvent) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    });
    return (
        <BrowserRouter>
            <ErrorBoundary>
                <ApolloProvider client={apolloClient}>
                    <ApolloHooksProvider client={apolloClient}>
                        <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY as string}>
                            <ScrollTop />
                            <ProfileContextProvider>
                                <Switch>
                                    <Route exact path="/">
                                        <Home />
                                    </Route>
                                    <Route exact path="/error">
                                        <ErrorPage />
                                    </Route>
                                    <ProtectedRoute path="/game-in-progress" component={GameInProgress} />
                                    <Route exact path="/code-invalid">
                                        <GameCodeInvalid />
                                    </Route>
                                    <Route exact path="/code-used">
                                        <GameCodeUsed />
                                    </Route>
                                    <Route exact path="/code-not-valid-yet">
                                        <GameCodeNotValidYet />
                                    </Route>
                                    <Route exact path="/code-expired">
                                        <GameCodeExpired />
                                    </Route>
                                    <ProtectedRoute path="/game-select" component={GameSelect} />
                                    {gamesConfiguration.map((gameConfiguration) => {
                                        return (
                                            <ProtectedRoute path={gameConfiguration.homeUrl} key={gameConfiguration.id}>
                                                <GameContextProvider>
                                                    <StopWatchContextProvider>
                                                        <Game
                                                            name={gameConfiguration.name}
                                                            lastStep={gameConfiguration.last}
                                                        />
                                                    </StopWatchContextProvider>
                                                </GameContextProvider>
                                            </ProtectedRoute>
                                        );
                                    })}
                                    <Route exact path="/login">
                                        <Login />
                                    </Route>
                                    <Route exact path="/detska-hra-reseni">
                                        <ZdarKidsWorkbookSolution />
                                    </Route>
                                    <PrivateRoute path="/admin">
                                        <Admin />
                                    </PrivateRoute>
                                    <Route exact path="/leaderboard">
                                        <Leaderboard />
                                    </Route>
                                    <Route>
                                        <RedirectToHome />
                                    </Route>
                                </Switch>
                            </ProfileContextProvider>
                        </GoogleReCaptchaProvider>
                    </ApolloHooksProvider>
                </ApolloProvider>
            </ErrorBoundary>
        </BrowserRouter>
    );
}

type Props = RouteProps & {
    children?: React.ReactNode;
};

type AuthProps = RouteProps & {
    children?: React.ReactNode;
    auth: boolean;
};

function getAuth({ children, auth, ...rest }: AuthProps) {
    if (rest.component) {
        return <Route {...rest} />;
    }
    return (
        <Route
            {...rest}
            render={({ location }) =>
                auth ? (
                    children
                ) : (
                    <Redirect
                        to={{
                            pathname: '/',
                            state: { from: location },
                        }}
                    />
                )
            }
        />
    );
}

function ProtectedRoute({ children, ...rest }: Props) {
    const { isInGame } = useContext(ProfileContext);
    const auth = isInGame;
    return getAuth({ children, auth, ...rest });
}

function PrivateRoute({ children, ...rest }: Props) {
    const { isAuthenticated } = useContext(ProfileContext);
    const auth = isAuthenticated;
    return getAuth({ children, auth, ...rest });
}
